import { type Role, roles, type UserIdentity } from "@groundspark/browser-auth";
import { type Pages, type PaperSize, paperSizes, sides } from "./leaflet-pages";
import { count, dailyHistory } from "./statistics";

export interface Branch {
  readonly id: string;
  readonly name: string;
}

export interface Group {
  readonly id: string;
  readonly name: string;
}

export interface Member extends UserIdentity {
  readonly phoneNumber: string;
  readonly groups: readonly Group[];
  readonly role: Role;
  readonly createdAt: Date;
}

export interface Page<T> {
  readonly items: readonly T[];
  readonly nextCursor: string | null;
}

export const channels = ["email", "text", "app_notification"] as const;
export type Channel = (typeof channels)[number];
export const channelLabels: Readonly<Record<Channel, string>> = {
  email: "Email",
  text: "Text",
  app_notification: "App notification",
};

export interface Communication {
  readonly id: string;
  readonly channel: Channel;
  readonly title: string | null;
  readonly body: string;
  readonly submittedAt: Date | null;
  readonly updatedAt: Date;
  readonly revision: number;
  readonly recipientCount: number;
}

export interface CommunicationDraft extends Communication {
  readonly previewText: string | null;
  readonly recipientIds: readonly string[];
}

export interface DraftContent {
  readonly title: string | null;
  readonly previewText: string | null;
  readonly body: string;
  readonly recipientIds: readonly string[];
}

export interface CommunicationDelivery {
  readonly userId: string;
  readonly displayName: string;
  readonly state: string;
  readonly readAt: Date | null;
  readonly pushQueued: number;
  readonly pushAccepted: number;
  readonly pushFailed: number;
  readonly pushCancelled: number;
}

export function parseDelivery(value: unknown): CommunicationDelivery {
  const record = object(value);
  const count = (name: string): number => {
    const value = record[name];
    if (!Number.isSafeInteger(value) || (value as number) < 0)
      throw new TypeError("Invalid delivery count");
    return value as number;
  };
  const state = text(record.state);
  if (!["queued", "accepted", "delivered", "failed", "cancelled"].includes(state))
    throw new TypeError("Invalid delivery state");
  return {
    userId: identifier(record.user_id),
    displayName: text(record.display_name),
    state,
    readAt: record.read_at === null ? null : date(record.read_at),
    pushQueued: count("push_queued"),
    pushAccepted: count("push_accepted"),
    pushFailed: count("push_failed"),
    pushCancelled: count("push_cancelled"),
  };
}

export function parseBranch(value: unknown): Branch {
  const record = object(value);
  return { id: identifier(record.id), name: text(record.name) };
}

export function parseMember(value: unknown): Member {
  const record = object(value);
  const role = record.role;
  if (!roles.some((candidate) => candidate === role))
    throw new TypeError("Invalid member role");
  const groups = parseList(record.groups, parseGroup);
  if (new Set(groups.map((group) => group.id)).size !== groups.length)
    throw new TypeError("Duplicate group memberships");
  return {
    id: identifier(record.id),
    email: text(record.email),
    displayName: text(record.display_name),
    phoneNumber: text(record.phone_number),
    groups,
    role: role as Role,
    createdAt: date(record.created_at),
  };
}

function parseGroup(value: unknown): Group {
  const record = object(value);
  return { id: identifier(record.id), name: text(record.name) };
}

export function parseCommunication(value: unknown): Communication {
  const record = object(value);
  const channel = record.channel;
  if (!channels.some((candidate) => candidate === channel))
    throw new TypeError("Invalid communication channel");
  const recipientCount = record.recipient_count;
  if (!Number.isSafeInteger(recipientCount) || (recipientCount as number) < 0)
    throw new TypeError("Invalid recipient count");
  if (!Number.isSafeInteger(record.revision) || (record.revision as number) < 1)
    throw new TypeError("Invalid draft revision");
  return {
    id: identifier(record.id),
    channel: channel as Channel,
    title: record.title === null ? null : content(record.title),
    body: content(record.body),
    submittedAt: record.submitted_at === null ? null : date(record.submitted_at),
    updatedAt: date(record.updated_at),
    revision: record.revision as number,
    recipientCount: recipientCount as number,
  };
}

export function parseDraft(value: unknown): CommunicationDraft {
  const record = object(value);
  const communication = parseCommunication(value);
  if (communication.submittedAt !== null) throw new TypeError("Expected a draft");
  return {
    ...communication,
    previewText: record.preview_text === null ? null : text(record.preview_text),
    recipientIds: parseList(record.recipient_ids, identifier),
  };
}

export interface Leaflet {
  readonly id: string;
  readonly name: string;
  readonly paperSize: PaperSize;
  readonly revision: number;
  readonly updatedAt: Date;
  readonly qrCode: LeafletQrCode;
  /** Scans of the QR code since the leaflet was created. */
  readonly scans: number;
}

export interface LeafletQrCode {
  /** The scan link that the QR code encodes. Opening it counts a scan. */
  readonly url: string;
  /** Where a scan leads. */
  readonly destination: string;
}

export interface LeafletDocument extends Leaflet {
  readonly pages: Pages;
}

/** Daily scans, in UTC, since the leaflet was created or for the last three months. */
export interface LeafletScans {
  readonly total: number;
  readonly history: readonly { readonly date: string; readonly scans: number }[];
}

export function parseLeaflet(value: unknown): Leaflet {
  const record = object(value);
  const paperSize = record.paper_size;
  if (!paperSizes.some((candidate) => candidate === paperSize))
    throw new TypeError("Invalid paper size");
  if (!Number.isSafeInteger(record.revision) || (record.revision as number) < 1)
    throw new TypeError("Invalid leaflet revision");
  const qrCode = object(record.qr_code);
  return {
    id: identifier(record.id),
    name: text(record.name),
    paperSize: paperSize as PaperSize,
    revision: record.revision as number,
    updatedAt: date(record.updated_at),
    qrCode: {
      url: webAddress(qrCode.url, ["http:", "https:"]),
      destination: webAddress(qrCode.destination, ["https:"]),
    },
    scans: count(record.scans),
  };
}

export function parseLeafletScans(value: unknown): LeafletScans {
  const record = object(value);
  const history = dailyHistory(record.history, (day) => ({ scans: count(day.scans) }));
  const total = count(record.total);
  if (history.reduce((sum, day) => sum + day.scans, 0) > total)
    throw new TypeError("Invalid scan total");
  return { total, history };
}

export function parseLeafletDocument(value: unknown): LeafletDocument {
  const pages = object(object(value).pages);
  const [front, back] = sides.map((side) => text(pages[side]));
  return { ...parseLeaflet(value), pages: { front: front!, back: back! } };
}

export function parsePage<T>(value: unknown, parseItem: (value: unknown) => T): Page<T> {
  const record = object(value);
  return {
    items: parseList(record.items, parseItem),
    nextCursor: record.next_cursor === null ? null : identifier(record.next_cursor),
  };
}

export function parseList<T>(value: unknown, parseItem: (value: unknown) => T): readonly T[] {
  if (!Array.isArray(value)) throw new TypeError("Expected a list");
  return value.map(parseItem);
}

export function identifier(value: unknown): string {
  const result = text(value);
  if (result.length > 64 || result.includes("/") || result.trim() !== result)
    throw new TypeError("Invalid identifier");
  return result;
}

function object(value: unknown): Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value))
    throw new TypeError("Expected an object");
  return value as Record<string, unknown>;
}

function text(value: unknown): string {
  if (typeof value !== "string" || value.length === 0)
    throw new TypeError("Expected non-empty text");
  return value;
}

function content(value: unknown): string {
  if (typeof value !== "string") throw new TypeError("Expected text");
  return value;
}

function webAddress(value: unknown, protocols: readonly string[]): string {
  const result = text(value);
  if (!URL.canParse(result) || !protocols.includes(new URL(result).protocol))
    throw new TypeError("Invalid web address");
  return result;
}

function date(value: unknown): Date {
  const result = new Date(text(value));
  if (Number.isNaN(result.getTime())) throw new TypeError("Invalid date");
  return result;
}
